import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { holidayHeaderTint } from "@/components/team/Holidays/schedule/holidayTints";
import { ScheduleHolidayRibbon } from "@/components/team/Holidays/schedule/ScheduleHolidayRibbon";
import type { ScheduleHoliday } from "@/components/team/Holidays/schedule/scheduleHolidays";
import { scheduleHolidayTooltip } from "@/components/team/Holidays/schedule/scheduleHolidayText";
import { useTextScale } from "@/hooks/useTextScale";
import { useWeekdayLabelFormat } from "@/hooks/useWeekdayLabelFormat";
import { FLUID_FONT_SIZE, FONT_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, STATUS, SURFACE } from "@/theme/tokens";
import { TOUCH_TARGET_HEIGHT_SX } from "@/ui/touchTarget";
import { formatStaffCount, isShownAsShort } from "./gapsDayModel";

type Props = {
	days: DateTime[];
	/**
	 * One shortfall per day, same order and length as `days` — or `undefined` while the grouping behind them
	 * is not final, when every day's gap line stays blank (its height is reserved either way).
	 */
	shortfalls: readonly number[] | undefined;
	selectedDate: DateTime;
	onSelectDate: (date: DateTime) => void;
	onPreviousWeek: () => void;
	onNextWeek: () => void;
	canGoBack: boolean;
	canGoForward: boolean;
	/**
	 * Days that can't be picked, drawn faded and taking no tap — the Post / Add date picker's past days (APP-886).
	 * Absent = every day can be picked, as on the home.
	 */
	isDayDisabled?: (day: DateTime) => boolean;
	/** A day's holiday, drawn as a corner ribbon on a tinted day. Absent = no day is marked, as in the Post / Add date picker. */
	holidayForDay?: (day: DateTime) => ScheduleHoliday | undefined;
	/** Test-id root: `{prefix}-strip`, `{prefix}-day-{iso}`, … The home's strip is `gaps-week`; a second strip on screen needs its own. */
	testIdPrefix?: string;
};

/**
 * Height the gap line under each day number occupies whether or not it has anything to say.
 *
 * Reserved rather than collapsed: the numbers appear and disappear as you page, and a row that
 * changes height on every tap makes the day you were aiming for move out from under your thumb.
 * Set by the line height alone, a multiple of the figure's size: Android's font size enlarges line heights with
 * the text, but not a `height` in ems, which clipped the digits. A day without a figure holds the line with a
 * non-breaking space.
 */
const GAP_LINE_HEIGHT = 1.4;
const EMPTY_GAP_LINE = "\u00A0";

/**
 * The strip's side controls are narrow on purpose. Seven day cells and two arrows share one row on a
 * 320px phone, so every control takes the finger-sized HEIGHT (`TOUCH_TARGET_HEIGHT_SX`) but
 * none takes a 44px minimum width — that sum is ~460px and pushed the row off the screen.
 */
const NAV_CONTROL_WIDTH = 22;

/**
 * The selected day's fill and inks cross-fade as the selection moves, rather than snapping from one
 * day to the next. Colour only — nothing moves, so there is no layout to settle — and switched off
 * under reduced motion.
 */
const selectionTransition = `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`;
const selectionTransitionSx = { transition: selectionTransition, "@media (prefers-reduced-motion: reduce)": { transition: "none" } } as const;

/**
 * The day number's circle at Android's default font size. It grows by the font size's scale, since the WebView enlarges
 * the number but not a width — in px, em or rem — around it.
 */
const DAY_NUMBER_CIRCLE_PX = 25;

const dayNumberSx = {
	aspectRatio: "1",
	display: "grid",
	placeItems: "center",
	borderRadius: "50%",
	fontSize: FONT_SIZE.TITLE_SM,
	fontWeight: 600,
	fontVariantNumeric: "tabular-nums",
} as const;

/**
 * The seven days of the loaded week, each carrying its own shortfall in staff.
 *
 * The point of the strip, in the artifact's words: Monday is −5 and you can see that without leaving
 * Thursday. A covered day shows a green ✓ rather than nothing — the artifact prints one and it is the
 * design that ships, so "all clear" is stated rather than inferred from an empty cell.
 *
 * Paging is pure front-end: `onPreviousWeek` / `onNextWeek` move to the same weekday in the neighbouring
 * loaded week, which the app already holds. No request fires, which is why the arrows can be this cheap.
 */
export const GapsWeekStrip: FC<Props> = ({
	days,
	shortfalls,
	selectedDate,
	onSelectDate,
	onPreviousWeek,
	onNextWeek,
	canGoBack,
	canGoForward,
	isDayDisabled,
	holidayForDay,
	// DEFAULT: the home's own strip, whose ids every suite already reads.
	testIdPrefix = "gaps-week",
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const weekdayFormat = useWeekdayLabelFormat();
	const textScale = useTextScale();
	const selectedISO = selectedDate.toISODate();

	const renderNav = useCallback(
		(direction: "previous" | "next") => {
			const isPrevious = direction === "previous";
			const disabled = isPrevious ? !canGoBack : !canGoForward;
			return (
				<ButtonBase
					data-testid={`${testIdPrefix}-${direction}`}
					aria-label={t(isPrevious ? "gaps.week.previous" : "gaps.week.next")}
					disabled={disabled}
					onClick={isPrevious ? onPreviousWeek : onNextWeek}
					sx={{ ...TOUCH_TARGET_HEIGHT_SX, width: NAV_CONTROL_WIDTH, borderRadius: 1, color: disabled ? INK.DISABLED : INK.MUTED, flex: "none" }}
				>
					{isPrevious ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
				</ButtonBase>
			);
		},
		[canGoBack, canGoForward, onPreviousWeek, onNextWeek, t, testIdPrefix],
	);

	return (
		<Box data-testid={`${testIdPrefix}-strip`} sx={{ display: "flex", alignItems: "stretch", px: 0.5, py: 0.75, bgcolor: SURFACE }}>
			{renderNav("previous")}
			{/* `minmax(0, 1fr)`, not `1fr`: a bare `1fr` track will not shrink below its content, so a wide
			    shortfall like `−111.6` would widen its day and push the row past the screen edge. */}
			<Box sx={{ flex: 1, minWidth: 0, display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
				{days.map((day, index) => {
					const iso = day.toISODate() ?? String(index);
					const isSelected = iso === selectedISO;
					const shortfall = shortfalls?.[index];
					const isShort = shortfall !== undefined && isShownAsShort(shortfall);
					const dateLabel = day.toFormat("cccc d LLLL");
					const isDisabled = isDayDisabled?.(day) === true;
					const scheduleHoliday = holidayForDay?.(day);
					const dayLabel =
						shortfall === undefined ? dateLabel : t(isShort ? "gaps.week.dayShort" : "gaps.week.dayCovered", { date: dateLabel, count: shortfall });
					return (
						<ButtonBase
							key={iso}
							data-testid={`${testIdPrefix}-day-${iso}`}
							aria-current={isSelected ? "date" : undefined}
							disabled={isDisabled}
							// The ribbon inside is hidden from screen readers, so the day's own label names the holiday.
							aria-label={scheduleHoliday ? t("gaps.week.dayHoliday", { day: dayLabel, holiday: scheduleHolidayTooltip(t, scheduleHoliday) }) : dayLabel}
							onClick={() => onSelectDate(day)}
							sx={{
								...TOUCH_TARGET_HEIGHT_SX,
								minWidth: 0,
								flexDirection: "column",
								gap: "1px",
								borderRadius: 1.5,
								py: 0.25,
								overflow: "hidden",
								bgcolor: scheduleHoliday ? holidayHeaderTint(theme) : undefined,
								"@media (hover: hover) and (pointer: fine)": { "&:hover": { bgcolor: NEUTRAL[50] } },
							}}
						>
							{scheduleHoliday && (
								<ScheduleHolidayRibbon scheduleHoliday={scheduleHoliday} decorative compact dataTestId={`${testIdPrefix}-holiday-ribbon-${iso}`} />
							)}
							<Typography
								component="span"
								sx={{
									fontSize: FONT_SIZE.LABEL_MICRO,
									letterSpacing: LETTER_SPACING.LABEL,
									textTransform: "uppercase",
									fontWeight: 600,
									// A day that can't be picked reads in the disabled ink the strip's arrows use.
									color: isSelected ? theme.palette.primary.dark : isDisabled ? INK.DISABLED : INK.MUTED,
									...selectionTransitionSx,
								}}
							>
								{day.toFormat(weekdayFormat)}
							</Typography>
							<Box
								component="span"
								sx={{
									...dayNumberSx,
									// Capped at the column, so the largest font sizes on a narrow phone still draw a circle, not an oval.
									width: `min(${DAY_NUMBER_CIRCLE_PX * textScale}px, 100%)`,
									bgcolor: isSelected ? theme.palette.primary.main : "transparent",
									color: isSelected ? SURFACE : isDisabled ? INK.DISABLED : INK.SECONDARY,
									...selectionTransitionSx,
								}}
							>
								{day.day}
							</Box>
							{/* Height is reserved above, so a covered day and a short one occupy the same box. */}
							<Typography
								component="span"
								data-testid={`${testIdPrefix}-day-gap-${iso}`}
								sx={{
									lineHeight: GAP_LINE_HEIGHT,
									// Seven values like `−118.2` share a 320px row (~34px each): the size steps down only there, a hair of
									// tracking keeps six characters in the column, and a 1px inset with clipping keeps neighbours apart
									// rather than running them together. The full count is in the cell's aria-label.
									fontSize: FLUID_FONT_SIZE.SEVEN_UP_FIGURE,
									letterSpacing: LETTER_SPACING.TIGHT_FIGURE,
									maxWidth: "100%",
									boxSizing: "border-box",
									px: "1px",
									overflow: "hidden",
									textOverflow: "clip",
									fontWeight: 600,
									fontVariantNumeric: "tabular-nums",
									whiteSpace: "nowrap",
									color: isShort ? STATUS.ERROR.main : STATUS.SUCCESS.main,
								}}
							>
								{shortfall === undefined ? EMPTY_GAP_LINE : isShort ? `−${formatStaffCount(shortfall)}` : "✓"}
							</Typography>
						</ButtonBase>
					);
				})}
			</Box>
			{renderNav("next")}
		</Box>
	);
};
